import {
  iosAudioGetCurrentTime,
  iosAudioGetDuration,
  iosAudioPause,
  iosAudioPlayFromUrl,
  iosAudioPlayStream,
  iosAudioPlayResume,
  iosAudioSeek,
  iosAudioSetPlaybackRate,
  iosAudioStop,
} from "../tools/ios/audio";
import { getFirebaseToken } from "../services/firebase";

/**
 * How the bytes for a track arrive. A `file` is a finished object with a real
 * duration; a `live-stream` is still being synthesized server-side, so it has
 * no length, no seeking, and only the native shell can consume it.
 */
export type AudioTrackKind = "file" | "live-stream";

// NOTE: This is a core complex component in the app.
// Do NOT touch without advising your lead first.

export class InternalAudioPlayer {
  isNativeIOS: boolean;
  _webPlayer: HTMLAudioElement | null;
  _isPlaying: boolean;
  _trackDuration: number;
  _playedChunks: number[][];
  _chunkStartTime: number;
  _chunkReachedTime: number;
  _progressInterval: ReturnType<typeof setInterval> | null;
  onPlay: (() => void) | null;
  onPause: (() => void) | null;
  onProgress: ((time: number) => void) | null;
  onEnd: (() => void) | null;
  _onTrackLoaded?: (duration: number) => void;
  // Bumped by every `setTrack` and `stop`. A native load spans several bridge
  // round trips on the ONE shell player; one that a newer load or a stop has
  // superseded must not pause, poll or report on the track that replaced it.
  _loadGeneration: number;

  constructor(
    isNativeIOS: boolean,
    onTrackLoaded?: (duration: number) => void
  ) {
    this.isNativeIOS = isNativeIOS;
    this.onPlay = null;
    this.onPause = null;
    this.onProgress = null;
    this.onEnd = null;
    // TODO: Remove once the rest of the app is refactored and the constructor updated.
    if (onTrackLoaded) this._onTrackLoaded = onTrackLoaded;
    this._webPlayer = null;
    this._isPlaying = false;
    this._trackDuration = 0;
    this._playedChunks = [];
    this._chunkStartTime = 0;
    this._chunkReachedTime = 0;
    this._progressInterval = null;
    this._loadGeneration = 0;
  }

  async setTrack(trackUrl: string, kind: AudioTrackKind) {
    const generation = ++this._loadGeneration;
    const superseded = (): boolean => this._loadGeneration !== generation;
    if (this.isNativeIOS) {
      // A live stream has no duration to wait for — the API is still
      // synthesizing. The shell fetches the URL itself (a `blob:` would be
      // meaningless to it), so hand over the auth the endpoint requires and
      // report "loaded" as soon as it accepts, with a 0 duration meaning
      // "unknown by design".
      if (kind === "live-stream") {
        const token = await getFirebaseToken();
        if (token === null) throw new Error("Not authenticated for streaming");

        if (superseded()) return;
        const started = await iosAudioPlayStream(trackUrl, {
          Authorization: `Bearer ${token}`,
        });
        if (superseded()) return;
        if (!started) {
          throw new Error("Shell rejected the audio stream");
        }
        this._trackDuration = 0;
        this._playedChunks = [];
        this._onTrackLoaded?.(0);
        return;
      }

      let success = false;
      let duration = 0;

      while (!success) {
        success = await iosAudioPlayFromUrl(trackUrl);
        if (superseded()) return;
      }
      while (duration <= 0) {
        duration = await iosAudioGetDuration();
        if (superseded()) return;
      }
      this._trackDuration = duration;
      await iosAudioPause();
      if (superseded()) return;
      this._onTrackLoaded?.(duration);
    } else {
      const audio = new Audio(trackUrl);
      // A replaced element can still fire (a stream it was buffering) and
      // the removeEventListener calls in `stop` cannot detach these
      // handlers. Only the current element may report its length, progress,
      // play or end, or an old track's land on the new one. Its pause (the
      // one `stop` gives it) reports only while the next track is not
      // playing yet: it tells the app nothing plays while that track loads,
      // or fails to, and must not stop it once it does.
      const isCurrent = (): boolean => this._webPlayer === audio;

      audio.addEventListener("play", () => {
        if (isCurrent() && this.onPlay !== null) this.onPlay();
      });
      audio.addEventListener("pause", () => {
        if (this.onPause === null) return;
        if (isCurrent() || !this._isPlaying) this.onPause();
      });

      // canplay fires when the browser has buffered enough data to begin
      // playback. This is the right moment to open the player panel and
      // trigger autoplay. loadedmetadata fires too early for streamed
      // audio (before any audio data has arrived).
      audio.addEventListener("canplay", () => {
        if (!isCurrent()) return;
        const d = audio.duration;
        this._trackDuration = Number.isFinite(d) && d > 0 ? d : 0;
        if (this._onTrackLoaded !== undefined) {
          this._onTrackLoaded(this._trackDuration);
        }
      });

      // Refine duration as the browser scans more frames during playback.
      audio.addEventListener("durationchange", () => {
        if (!isCurrent()) return;
        const d = audio.duration;
        if (Number.isFinite(d) && d > 0) {
          this._trackDuration = d;
          if (this._onTrackLoaded !== undefined) this._onTrackLoaded(d);
        }
      });

      audio.addEventListener("timeupdate", () => {
        if (isCurrent()) this.handleProgress();
      });

      audio.addEventListener("ended", () => {
        if (isCurrent() && this.onEnd !== null) this.onEnd();
      });

      audio.load();
      this._webPlayer = audio;
    }
    this._playedChunks = [];
  }

  async play() {
    if (this.isNativeIOS) {
      iosAudioPlayResume();
      this._chunkStartTime = await iosAudioGetCurrentTime();
    } else {
      if (this._webPlayer !== null) {
        try {
          await this._webPlayer.play();
        } catch (error) {
          if (!(error instanceof DOMException)) throw error;
          // iOS Safari throws DOMException (NotReadableError / AbortError /
          // NotAllowedError) when audio I/O is interrupted, playback is
          // cancelled, or autoplay is blocked. Nothing app can do — swallow to
          // prevent unhandled rejections. (JUISCI-APP-1WA)
          return;
        }
        if (this._webPlayer.currentTime) {
          this._chunkStartTime = this._webPlayer.currentTime;
        }
      }
    }

    if (this._progressInterval !== null) clearInterval(this._progressInterval);
    this._progressInterval = setInterval(() => {
      this.handleProgress();
    }, 500);

    this._isPlaying = true;
  }

  pause() {
    this.updateChunks();
    this.isNativeIOS ? iosAudioPause() : this._webPlayer?.pause();
    if (this._progressInterval !== null) clearInterval(this._progressInterval);

    this._isPlaying = false;
  }

  async stop() {
    this._loadGeneration++;
    this.updateChunks();
    if (this.isNativeIOS) {
      await iosAudioStop();
    } else {
      if (this._webPlayer) {
        this._webPlayer.pause();
        this._webPlayer.currentTime = 0;
        this._webPlayer.removeEventListener("loadedmetadata", () => {});
        this._webPlayer.removeEventListener("ended", () => {});
        this._webPlayer.removeEventListener("timeupdate", () => {});
        this._webPlayer = null;
      }
    }

    this._trackDuration = -1;
    if (this._progressInterval !== null) clearInterval(this._progressInterval);

    this._isPlaying = false;
  }

  seek(time: number) {
    if (this._isPlaying && Math.abs(time - this._chunkReachedTime) >= 2) {
      this.updateChunks();
    }
    this._chunkStartTime = time;
    this._chunkReachedTime = time;

    if (this.isNativeIOS) {
      iosAudioSeek(time);
    } else {
      if (this._webPlayer) this._webPlayer.currentTime = time;
    }
  }

  setPlaybackRate(rate: number) {
    if (this.isNativeIOS) {
      iosAudioSetPlaybackRate(rate);
    } else {
      if (this._webPlayer) this._webPlayer.playbackRate = rate;
    }
  }

  async handleProgress() {
    const time = this.isNativeIOS
      ? await iosAudioGetCurrentTime()
      : this._webPlayer?.currentTime;

    if (time && this.onProgress) {
      this.onProgress(time);
      this._chunkReachedTime = time;
    }
    this.updateChunks();
  }

  updateChunks() {
    const start = Math.floor(this._chunkStartTime);
    const end = Math.ceil(this._chunkReachedTime);
    if (start >= end) return;

    const allChunks = [...this._playedChunks, [start, end]];

    // Sort chunks by start time
    allChunks.sort((a, b) => (a.at(0) ?? 0) - (b.at(0) ?? 0));

    // Merge overlapping or adjacent chunks
    const mergedChunks: number[][] = [];
    for (const chunk of allChunks) {
      const chunkStart = chunk[0];
      const chunkEnd = chunk[1];
      if (chunkStart === undefined || chunkEnd === undefined) continue;

      const lastChunk = mergedChunks.at(-1);
      if (lastChunk === undefined) {
        mergedChunks.push(chunk);
      } else {
        const lastChunkEnd = lastChunk[1];
        if (lastChunkEnd !== undefined && chunkStart <= lastChunkEnd) {
          lastChunk[1] = Math.max(lastChunkEnd, chunkEnd);
        } else {
          mergedChunks.push(chunk);
        }
      }
    }

    this._playedChunks = mergedChunks;
  }
}
